import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useEditResourcesListingConfiguration,
  useRetrieveCurrentPlanzone,
  useRetrieveResourcesListingConfiguration,
  useRolesRetrieval,
} from "@planzone/view-models";
import { Id, ResourcesListingConfiguration as Config } from "@planzone/dto";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { HorizontalList } from "@common/horizontalList";
import { Form } from "./form";
import { Props } from "./types";
import styles from "./styles.scss";

function canSubmitForm(profile: Config[number]) {
  return profile.roles.length > 0 && profile.columns.length > 0;
}

function ResourcesListingConfiguration({ cancel }: Props) {
  const { t } = useTranslation();
  const { roles } = useRolesRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { configuration } = useRetrieveResourcesListingConfiguration(planzone);
  const {
    isRequestPending: isEditionRequestPending,
    editResourcesListingConfiguration,
  } = useEditResourcesListingConfiguration(planzone);
  const [formData, setFormData] = React.useState(configuration);
  const [profileToAdd, setProfileToAdd] = React.useState<Config[number] | null>(
    null,
  );
  const [currentProfile, setCurrentProfile] = React.useState(0);
  const usedRoles = formData
    .reduce<Id[]>((roles, profile) => [...roles, ...profile.roles], [])
    .filter((role) =>
      !profileToAdd && formData[currentProfile]
        ? !formData[currentProfile].roles.find((r) => r === role)
        : true,
    );
  const remainingRoles = roles.filter(
    (role) =>
      ![
        ...(formData[currentProfile] ? formData[currentProfile].roles : []),
        ...usedRoles,
      ].find((id) => id === role.name || role.uuid === id),
  );
  const canSubmit = canSubmitForm(
    !!profileToAdd
      ? profileToAdd
      : !!formData[currentProfile]
        ? formData[currentProfile]
        : {
            name: "",
            roles: [],
            columns: [],
          },
  );

  useMessage(({ type, data }) => {
    if (type === events.editResourcesListingConfigurationSucceeded) {
      setProfileToAdd(null);
      data.configuration.length !== configuration.length &&
        setCurrentProfile(data.configuration.length - 1);
    }
  });

  React.useEffect(() => {
    setFormData(configuration);
  }, [configuration]);

  return (
    <Modal header={t("resourcesListingConfiguration.title")} close={cancel}>
      <HorizontalList>
        <div className={styles.tabs}>
          {remainingRoles.length > 0 && (
            <p
              className={classNames(styles.tab, {
                [styles.selectedTab]: !!profileToAdd,
              })}
              onClick={() =>
                setProfileToAdd({
                  name: "",
                  roles: remainingRoles.map(({ name }) => name),
                  columns: [
                    { key: "tags" },
                    { key: "role" },
                    { key: "invitationStatus" },
                  ],
                })
              }
            >
              + {t("resourcesListingConfiguration.addProfile")}
            </p>
          )}
          {formData.map(({ name }, index) => (
            <p
              key={index}
              className={classNames(styles.tab, {
                [styles.selectedTab]: index === currentProfile && !profileToAdd,
              })}
              onClick={() => {
                setProfileToAdd(null);
                setCurrentProfile(index);
              }}
            >
              {name}
            </p>
          ))}
        </div>
      </HorizontalList>
      <form
        className={styles.resourcesListingConfiguration}
        onSubmit={(e) => {
          e.preventDefault();

          !isEditionRequestPending &&
            editResourcesListingConfiguration({
              planzone,
              configuration,
              nextConfiguration: !profileToAdd
                ? formData
                : [...formData, profileToAdd],
            });
        }}
      >
        {!!formData[currentProfile] && !profileToAdd && (
          <div className={styles.form}>
            <Form
              formData={formData[currentProfile]}
              usedRoles={usedRoles}
              edit={(profile) =>
                setFormData([
                  ...formData.slice(0, currentProfile),
                  profile,
                  ...formData.slice(currentProfile + 1),
                ])
              }
            />
          </div>
        )}
        {!!profileToAdd && (
          <div className={styles.form}>
            <Form
              formData={profileToAdd}
              usedRoles={usedRoles}
              edit={(profile) => setProfileToAdd(profile)}
            />
          </div>
        )}
        <div className={styles.footer}>
          {!profileToAdd && (
            <div>
              <Button
                type="delete"
                status={isEditionRequestPending ? "pending" : "none"}
                isDisabled={isEditionRequestPending}
                press={() => {
                  editResourcesListingConfiguration({
                    planzone,
                    configuration,
                    nextConfiguration: [
                      ...configuration.slice(0, currentProfile),
                      ...configuration.slice(currentProfile + 1),
                    ],
                  });
                  setCurrentProfile(0);
                }}
              >
                {t("resourcesListingConfiguration.remove")}
              </Button>
            </div>
          )}
          <div>
            <Button type="cancel" press={cancel}>
              {t("resourcesListingConfiguration.cancel")}
            </Button>
            <Button
              type="submit"
              status={isEditionRequestPending ? "pending" : "none"}
              isDisabled={isEditionRequestPending || !canSubmit}
            >
              {!profileToAdd
                ? t("resourcesListingConfiguration.edit")
                : t("resourcesListingConfiguration.add")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export { ResourcesListingConfiguration };
